import * as z from 'zod';

export const ListPublicPlansRequest = z.object({
  options: z
    .object({
      limit: z
        .number()
        .int()
        .describe(
          'Number of items to list. Defaults to `75`. See [limits](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#paging).'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe(
          'Number of items to skip. Defaults to `0`. See [paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#paging).'
        )
        .min(0)
        .optional()
        .nullable(),
      planIds: z.array(z.string()).max(100).optional(),
    })
    .describe('Options for filtering and paging the list of public plans. ')
    .optional(),
});
export const ListPublicPlansResponse = z.object({
  plans: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Plan ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        name: z
          .string()
          .describe('Plan name.')
          .min(1)
          .max(50)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe('Plan description.')
          .max(450)
          .optional()
          .nullable(),
        perks: z
          .object({ values: z.array(z.string()).max(500).optional() })
          .describe(
            'What is included with this plan (for example, 1 weekly entrance to a specific class).'
          )
          .optional(),
        pricing: z
          .intersection(
            z.object({
              price: z
                .object({
                  value: z
                    .string()
                    .describe(
                      "Monetary amount. Decimal string with a period as a decimal separator. For example, `'3.99'`. Can't be negative."
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      "Currency code. Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `'USD'`."
                    )
                    .optional(),
                })
                .describe(
                  'Amount for a single payment. For subscriptions, this is the amount to pay each payment cycle and it is required. For plans that are not recurring plans, it is the single payment amount for the whole subscription.'
                )
                .optional(),
              freeTrialDays: z
                .number()
                .int()
                .describe(
                  'Free trial period for the plan in days. Available only for recurring plans, meaning plans whose pricing model is `subscription`. Set to `0` to remove the free trial.'
                )
                .min(0)
                .max(999)
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                subscription: z.never().optional(),
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
              }),
              z.object({
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
                subscription: z
                  .object({
                    cycleDuration: z
                      .object({
                        count: z
                          .number()
                          .int()
                          .describe(
                            'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                          )
                          .min(1)
                          .optional()
                          .nullable(),
                        unit: z
                          .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                          .describe('Unit of time for the cycle duration.')
                          .optional(),
                      })
                      .describe(
                        'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                      )
                      .optional(),
                    cycleCount: z
                      .number()
                      .int()
                      .describe(
                        "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                      )
                      .min(0)
                      .max(2000)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Pricing model indicating that the plan has recurring payments. This type of subscription is not a "Wix subscription," which encompasses various types of subscriptions, such as Wix Stores subscriptions, Wix invoice subscriptions, and all pricing plan models.'
                  ),
              }),
              z.object({
                subscription: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
                singlePaymentForDuration: z
                  .object({
                    count: z
                      .number()
                      .int()
                      .describe(
                        'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    unit: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe('Unit of time for the cycle duration.')
                      .optional(),
                  })
                  .describe(
                    'Pricing model indicating a single payment per cycle and the length of the cycle. The cycle is the duration of the plan, not a payment cycle.'
                  ),
              }),
              z.object({
                subscription: z.never().optional(),
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z
                  .boolean()
                  .describe(
                    'Pricing model indicating the plan is paid in a single payment. The plan is valid until canceled.'
                  ),
              }),
            ])
          )
          .describe('Plan price, payment schedule, and expiration.')
          .optional(),
        primary: z
          .boolean()
          .describe('Whether the plan is marked as primary.')
          .optional(),
        _createdDate: z
          .date()
          .describe('Date plan was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date plan was last updated.')
          .optional()
          .nullable(),
        slug: z
          .string()
          .describe(
            'URL-friendly version of plan name. Unique across all plans in the same site.'
          )
          .min(1)
          .optional()
          .nullable(),
        maxPurchasesPerBuyer: z
          .number()
          .int()
          .describe(
            'Number of times the same buyer can purchase the plan. An empty value or a value of 0 means no limitation.'
          )
          .min(0)
          .optional()
          .nullable(),
        allowFutureStartDate: z
          .boolean()
          .describe(
            'Whether the buyer can start the plan at a later date. Defaults to false.'
          )
          .optional()
          .nullable(),
        buyerCanCancel: z
          .boolean()
          .describe(
            'Whether the buyer is allowed to cancel their plan. Defaults to false.'
          )
          .optional()
          .nullable(),
        termsAndConditions: z
          .string()
          .describe(
            'Any terms and conditions that apply to the plan. This information is displayed during checkout.'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            'ID of the form associated with the plan at checkout.\n\nLearn more about [forms](https://support.wix.com/en/article/wix-forms-an-overview).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe(
      'Object containing paging-related data (such as the number of plans returned, the offset, and so on).'
    )
    .optional(),
});
export const QueryPublicPlansRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          slug: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          primary: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum(['_id', 'slug', '_createdDate', '_updatedDate', 'primary'])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
});
export const QueryPublicPlansResponse = z.object({
  plans: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Plan ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        name: z
          .string()
          .describe('Plan name.')
          .min(1)
          .max(50)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe('Plan description.')
          .max(450)
          .optional()
          .nullable(),
        perks: z
          .object({ values: z.array(z.string()).max(500).optional() })
          .describe(
            'What is included with this plan (for example, 1 weekly entrance to a specific class).'
          )
          .optional(),
        pricing: z
          .intersection(
            z.object({
              price: z
                .object({
                  value: z
                    .string()
                    .describe(
                      "Monetary amount. Decimal string with a period as a decimal separator. For example, `'3.99'`. Can't be negative."
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      "Currency code. Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `'USD'`."
                    )
                    .optional(),
                })
                .describe(
                  'Amount for a single payment. For subscriptions, this is the amount to pay each payment cycle and it is required. For plans that are not recurring plans, it is the single payment amount for the whole subscription.'
                )
                .optional(),
              freeTrialDays: z
                .number()
                .int()
                .describe(
                  'Free trial period for the plan in days. Available only for recurring plans, meaning plans whose pricing model is `subscription`. Set to `0` to remove the free trial.'
                )
                .min(0)
                .max(999)
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                subscription: z.never().optional(),
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
              }),
              z.object({
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
                subscription: z
                  .object({
                    cycleDuration: z
                      .object({
                        count: z
                          .number()
                          .int()
                          .describe(
                            'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                          )
                          .min(1)
                          .optional()
                          .nullable(),
                        unit: z
                          .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                          .describe('Unit of time for the cycle duration.')
                          .optional(),
                      })
                      .describe(
                        'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                      )
                      .optional(),
                    cycleCount: z
                      .number()
                      .int()
                      .describe(
                        "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                      )
                      .min(0)
                      .max(2000)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Pricing model indicating that the plan has recurring payments. This type of subscription is not a "Wix subscription," which encompasses various types of subscriptions, such as Wix Stores subscriptions, Wix invoice subscriptions, and all pricing plan models.'
                  ),
              }),
              z.object({
                subscription: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
                singlePaymentForDuration: z
                  .object({
                    count: z
                      .number()
                      .int()
                      .describe(
                        'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    unit: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe('Unit of time for the cycle duration.')
                      .optional(),
                  })
                  .describe(
                    'Pricing model indicating a single payment per cycle and the length of the cycle. The cycle is the duration of the plan, not a payment cycle.'
                  ),
              }),
              z.object({
                subscription: z.never().optional(),
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z
                  .boolean()
                  .describe(
                    'Pricing model indicating the plan is paid in a single payment. The plan is valid until canceled.'
                  ),
              }),
            ])
          )
          .describe('Plan price, payment schedule, and expiration.')
          .optional(),
        primary: z
          .boolean()
          .describe('Whether the plan is marked as primary.')
          .optional(),
        _createdDate: z
          .date()
          .describe('Date plan was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date plan was last updated.')
          .optional()
          .nullable(),
        slug: z
          .string()
          .describe(
            'URL-friendly version of plan name. Unique across all plans in the same site.'
          )
          .min(1)
          .optional()
          .nullable(),
        maxPurchasesPerBuyer: z
          .number()
          .int()
          .describe(
            'Number of times the same buyer can purchase the plan. An empty value or a value of 0 means no limitation.'
          )
          .min(0)
          .optional()
          .nullable(),
        allowFutureStartDate: z
          .boolean()
          .describe(
            'Whether the buyer can start the plan at a later date. Defaults to false.'
          )
          .optional()
          .nullable(),
        buyerCanCancel: z
          .boolean()
          .describe(
            'Whether the buyer is allowed to cancel their plan. Defaults to false.'
          )
          .optional()
          .nullable(),
        termsAndConditions: z
          .string()
          .describe(
            'Any terms and conditions that apply to the plan. This information is displayed during checkout.'
          )
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            'ID of the form associated with the plan at checkout.\n\nLearn more about [forms](https://support.wix.com/en/article/wix-forms-an-overview).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe(
      'Object containing paging-related data (such as the number of plans returned, the offset, and so on).'
    )
    .optional(),
});
export const GetPlanRequest = z.object({
  _id: z
    .string()
    .describe('Plan ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetPlanResponse = z.object({
  _id: z
    .string()
    .describe('Plan ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  name: z.string().describe('Plan name.').min(1).max(50).optional().nullable(),
  description: z
    .string()
    .describe('Plan description.')
    .max(450)
    .optional()
    .nullable(),
  perks: z
    .object({ values: z.array(z.string()).max(500).optional() })
    .describe(
      'List of text strings that promote what is included with this plan. For example, "Plenty of parking" or "Free gift on your birthday."'
    )
    .optional(),
  pricing: z
    .intersection(
      z.object({
        price: z
          .object({
            value: z
              .string()
              .describe(
                "Monetary amount. Decimal string with a period as a decimal separator. For example, `'3.99'`. Can't be negative."
              )
              .optional(),
            currency: z
              .string()
              .describe(
                "Currency code. Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `'USD'`."
              )
              .optional(),
          })
          .describe(
            'Amount for a single payment. For subscriptions, this is the amount to pay each payment cycle and it is required. For plans that are not recurring plans, it is the single payment amount for the whole subscription.'
          )
          .optional(),
        freeTrialDays: z
          .number()
          .int()
          .describe(
            'Free trial period for the plan in days. Available only for recurring plans, meaning plans whose pricing model is `subscription`. Set to `0` to remove the free trial.'
          )
          .min(0)
          .max(999)
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          subscription: z.never().optional(),
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
        }),
        z.object({
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
          subscription: z
            .object({
              cycleDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                )
                .optional(),
              cycleCount: z
                .number()
                .int()
                .describe(
                  "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                )
                .min(0)
                .max(2000)
                .optional()
                .nullable(),
            })
            .describe(
              'Pricing model indicating that the plan has recurring payments. This type of subscription is not a "Wix subscription," which encompasses various types of subscriptions, such as Wix Stores subscriptions, Wix invoice subscriptions, and all pricing plan models.'
            ),
        }),
        z.object({
          subscription: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
          singlePaymentForDuration: z
            .object({
              count: z
                .number()
                .int()
                .describe(
                  'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                )
                .min(1)
                .optional()
                .nullable(),
              unit: z
                .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                .describe('Unit of time for the cycle duration.')
                .optional(),
            })
            .describe(
              'Pricing model indicating a single payment per cycle and the length of the cycle. The cycle is the duration of the plan, not a payment cycle.'
            ),
        }),
        z.object({
          subscription: z.never().optional(),
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z
            .boolean()
            .describe(
              'Pricing model indicating the plan is paid in a single payment. The plan is valid until canceled.'
            ),
        }),
      ])
    )
    .describe('Plan price, payment schedule, and expiration.')
    .optional(),
  public: z
    .boolean()
    .describe(
      'Whether the plan is public (visible to site visitors and members).'
    )
    .optional()
    .nullable(),
  archived: z
    .boolean()
    .describe(
      "Whether the plan is archived. Archived plans are not visible and can't be purchased anymore, but existing purchases remain in effect."
    )
    .optional(),
  primary: z
    .boolean()
    .describe('Whether the plan is marked as primary.')
    .optional(),
  hasOrders: z
    .boolean()
    .describe(
      'Whether the plan has any orders (including pending and unpaid orders).'
    )
    .optional(),
  _createdDate: z
    .date()
    .describe('Date plan was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date plan was last updated.')
    .optional()
    .nullable(),
  slug: z
    .string()
    .describe(
      'URL-friendly version of plan name. Unique across all plans in the same site.'
    )
    .min(1)
    .optional()
    .nullable(),
  maxPurchasesPerBuyer: z
    .number()
    .int()
    .describe(
      'Number of times the same buyer can purchase the plan. Currently limited to support:\n- Empty value or a value of `0`, meaning no limitation.\n- Value of `1`, meaning limited to 1 purchase per buyer.'
    )
    .min(0)
    .max(1)
    .optional()
    .nullable(),
  allowFutureStartDate: z
    .boolean()
    .describe(
      'Whether the buyer can start the plan at a later date. Defaults to false.'
    )
    .optional()
    .nullable(),
  buyerCanCancel: z
    .boolean()
    .describe(
      'Whether the buyer is allowed to cancel their plan. Defaults to false.\nIf false, calling Request Cancellation returns an error.'
    )
    .optional()
    .nullable(),
  termsAndConditions: z
    .string()
    .describe(
      'Any terms and conditions that apply to the plan. This information is displayed during checkout.'
    )
    .max(3000)
    .optional()
    .nullable(),
  formId: z
    .string()
    .describe(
      'ID of the form associated with the plan at checkout.\n\nLearn more about [forms](https://support.wix.com/en/article/wix-forms-an-overview).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
});
export const ListPlansRequest = z.object({
  options: z
    .object({
      archived: z
        .enum(['ACTIVE', 'ARCHIVED', 'ARCHIVED_AND_ACTIVE'])
        .optional(),
      public: z.enum(['PUBLIC_AND_HIDDEN', 'PUBLIC', 'HIDDEN']).optional(),
      limit: z
        .number()
        .int()
        .describe('Number of pricing plans to list. Defaults to `75`.')
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Number of pricing plans to skip. Defaults to `0`.')
        .min(0)
        .optional()
        .nullable(),
      planIds: z.array(z.string()).max(100).optional(),
    })
    .describe('Options for filtering and paging the list of plans. ')
    .optional(),
});
export const ListPlansResponse = z.object({
  plans: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Plan ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        name: z
          .string()
          .describe('Plan name.')
          .min(1)
          .max(50)
          .optional()
          .nullable(),
        description: z
          .string()
          .describe('Plan description.')
          .max(450)
          .optional()
          .nullable(),
        perks: z
          .object({ values: z.array(z.string()).max(500).optional() })
          .describe(
            'List of text strings that promote what is included with this plan. For example, "Plenty of parking" or "Free gift on your birthday."'
          )
          .optional(),
        pricing: z
          .intersection(
            z.object({
              price: z
                .object({
                  value: z
                    .string()
                    .describe(
                      "Monetary amount. Decimal string with a period as a decimal separator. For example, `'3.99'`. Can't be negative."
                    )
                    .optional(),
                  currency: z
                    .string()
                    .describe(
                      "Currency code. Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `'USD'`."
                    )
                    .optional(),
                })
                .describe(
                  'Amount for a single payment. For subscriptions, this is the amount to pay each payment cycle and it is required. For plans that are not recurring plans, it is the single payment amount for the whole subscription.'
                )
                .optional(),
              freeTrialDays: z
                .number()
                .int()
                .describe(
                  'Free trial period for the plan in days. Available only for recurring plans, meaning plans whose pricing model is `subscription`. Set to `0` to remove the free trial.'
                )
                .min(0)
                .max(999)
                .optional()
                .nullable(),
            }),
            z.xor([
              z.object({
                subscription: z.never().optional(),
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
              }),
              z.object({
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
                subscription: z
                  .object({
                    cycleDuration: z
                      .object({
                        count: z
                          .number()
                          .int()
                          .describe(
                            'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                          )
                          .min(1)
                          .optional()
                          .nullable(),
                        unit: z
                          .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                          .describe('Unit of time for the cycle duration.')
                          .optional(),
                      })
                      .describe(
                        'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                      )
                      .optional(),
                    cycleCount: z
                      .number()
                      .int()
                      .describe(
                        "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                      )
                      .min(0)
                      .max(2000)
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Pricing model indicating that the plan has recurring payments. This type of subscription is not a "Wix subscription," which encompasses various types of subscriptions, such as Wix Stores subscriptions, Wix invoice subscriptions, and all pricing plan models.'
                  ),
              }),
              z.object({
                subscription: z.never().optional(),
                singlePaymentUnlimited: z.never().optional(),
                singlePaymentForDuration: z
                  .object({
                    count: z
                      .number()
                      .int()
                      .describe(
                        'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                      )
                      .min(1)
                      .optional()
                      .nullable(),
                    unit: z
                      .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                      .describe('Unit of time for the cycle duration.')
                      .optional(),
                  })
                  .describe(
                    'Pricing model indicating a single payment per cycle and the length of the cycle. The cycle is the duration of the plan, not a payment cycle.'
                  ),
              }),
              z.object({
                subscription: z.never().optional(),
                singlePaymentForDuration: z.never().optional(),
                singlePaymentUnlimited: z
                  .boolean()
                  .describe(
                    'Pricing model indicating the plan is paid in a single payment. The plan is valid until canceled.'
                  ),
              }),
            ])
          )
          .describe('Plan price, payment schedule, and expiration.')
          .optional(),
        public: z
          .boolean()
          .describe(
            'Whether the plan is public (visible to site visitors and members).'
          )
          .optional()
          .nullable(),
        archived: z
          .boolean()
          .describe(
            "Whether the plan is archived. Archived plans are not visible and can't be purchased anymore, but existing purchases remain in effect."
          )
          .optional(),
        primary: z
          .boolean()
          .describe('Whether the plan is marked as primary.')
          .optional(),
        hasOrders: z
          .boolean()
          .describe(
            'Whether the plan has any orders (including pending and unpaid orders).'
          )
          .optional(),
        _createdDate: z
          .date()
          .describe('Date plan was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date plan was last updated.')
          .optional()
          .nullable(),
        slug: z
          .string()
          .describe(
            'URL-friendly version of plan name. Unique across all plans in the same site.'
          )
          .min(1)
          .optional()
          .nullable(),
        maxPurchasesPerBuyer: z
          .number()
          .int()
          .describe(
            'Number of times the same buyer can purchase the plan. Currently limited to support:\n- Empty value or a value of `0`, meaning no limitation.\n- Value of `1`, meaning limited to 1 purchase per buyer.'
          )
          .min(0)
          .max(1)
          .optional()
          .nullable(),
        allowFutureStartDate: z
          .boolean()
          .describe(
            'Whether the buyer can start the plan at a later date. Defaults to false.'
          )
          .optional()
          .nullable(),
        buyerCanCancel: z
          .boolean()
          .describe(
            'Whether the buyer is allowed to cancel their plan. Defaults to false.\nIf false, calling Request Cancellation returns an error.'
          )
          .optional()
          .nullable(),
        termsAndConditions: z
          .string()
          .describe(
            'Any terms and conditions that apply to the plan. This information is displayed during checkout.'
          )
          .max(3000)
          .optional()
          .nullable(),
        formId: z
          .string()
          .describe(
            'ID of the form associated with the plan at checkout.\n\nLearn more about [forms](https://support.wix.com/en/article/wix-forms-an-overview).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe(
      'Object containing paging-related data (such as the number of plans returned, the offset, and so on).'
    )
    .optional(),
});
export const GetPlanStatsRequest = z.object({});
export const GetPlanStatsResponse = z.object({
  totalPlans: z
    .number()
    .int()
    .describe(
      'Total number of plans created, including active plans (both public and hidden) and archived plans.'
    )
    .optional(),
});
export const CreatePlanRequest = z.object({
  plan: z
    .object({
      _id: z
        .string()
        .describe('Plan ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      name: z.string().describe('Plan name.').min(1).max(50),
      description: z
        .string()
        .describe('Plan description.')
        .max(450)
        .optional()
        .nullable(),
      perks: z
        .object({ values: z.array(z.string()).max(500).optional() })
        .describe(
          'List of text strings that promote what is included with this plan. For example, "Plenty of parking" or "Free gift on your birthday."'
        )
        .optional(),
      pricing: z
        .intersection(
          z.object({
            price: z
              .object({
                value: z
                  .string()
                  .describe(
                    "Monetary amount. Decimal string with a period as a decimal separator. For example, `'3.99'`. Can't be negative."
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    "Currency code. Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `'USD'`."
                  )
                  .optional(),
              })
              .describe(
                'Amount for a single payment. For subscriptions, this is the amount to pay each payment cycle and it is required. For plans that are not recurring plans, it is the single payment amount for the whole subscription.'
              )
              .optional(),
            freeTrialDays: z
              .number()
              .int()
              .describe(
                'Free trial period for the plan in days. Available only for recurring plans, meaning plans whose pricing model is `subscription`. Set to `0` to remove the free trial.'
              )
              .min(0)
              .max(999)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              subscription: z
                .object({
                  cycleDuration: z
                    .object({
                      count: z
                        .number()
                        .int()
                        .describe(
                          'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      unit: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .optional(),
                    })
                    .describe(
                      'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                    )
                    .optional(),
                  cycleCount: z
                    .number()
                    .int()
                    .describe(
                      "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                    )
                    .min(0)
                    .max(2000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Pricing model indicating that the plan has recurring payments. This type of subscription is not a "Wix subscription," which encompasses various types of subscriptions, such as Wix Stores subscriptions, Wix invoice subscriptions, and all pricing plan models.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              singlePaymentForDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .optional(),
                })
                .describe(
                  'Pricing model indicating a single payment per cycle and the length of the cycle. The cycle is the duration of the plan, not a payment cycle.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z
                .boolean()
                .describe(
                  'Pricing model indicating the plan is paid in a single payment. The plan is valid until canceled.'
                ),
            }),
          ])
        )
        .describe('Plan price, payment schedule, and expiration.'),
      public: z
        .boolean()
        .describe(
          'Whether the plan is public (visible to site visitors and members).'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the plan is archived. Archived plans are not visible and can't be purchased anymore, but existing purchases remain in effect."
        )
        .optional(),
      primary: z
        .boolean()
        .describe('Whether the plan is marked as primary.')
        .optional(),
      hasOrders: z
        .boolean()
        .describe(
          'Whether the plan has any orders (including pending and unpaid orders).'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date plan was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date plan was last updated.')
        .optional()
        .nullable(),
      slug: z
        .string()
        .describe(
          'URL-friendly version of plan name. Unique across all plans in the same site.'
        )
        .min(1)
        .optional()
        .nullable(),
      maxPurchasesPerBuyer: z
        .number()
        .int()
        .describe(
          'Number of times the same buyer can purchase the plan. Currently limited to support:\n- Empty value or a value of `0`, meaning no limitation.\n- Value of `1`, meaning limited to 1 purchase per buyer.'
        )
        .min(0)
        .max(1)
        .optional()
        .nullable(),
      allowFutureStartDate: z
        .boolean()
        .describe(
          'Whether the buyer can start the plan at a later date. Defaults to false.'
        )
        .optional()
        .nullable(),
      buyerCanCancel: z
        .boolean()
        .describe(
          'Whether the buyer is allowed to cancel their plan. Defaults to false.\nIf false, calling Request Cancellation returns an error.'
        )
        .optional()
        .nullable(),
      termsAndConditions: z
        .string()
        .describe(
          'Any terms and conditions that apply to the plan. This information is displayed during checkout.'
        )
        .max(3000)
        .optional()
        .nullable(),
      formId: z
        .string()
        .describe(
          'ID of the form associated with the plan at checkout.\n\nLearn more about [forms](https://support.wix.com/en/article/wix-forms-an-overview).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe('Information for the plan being created. '),
});
export const CreatePlanResponse = z.object({
  _id: z
    .string()
    .describe('Plan ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  name: z.string().describe('Plan name.').min(1).max(50).optional().nullable(),
  description: z
    .string()
    .describe('Plan description.')
    .max(450)
    .optional()
    .nullable(),
  perks: z
    .object({ values: z.array(z.string()).max(500).optional() })
    .describe(
      'List of text strings that promote what is included with this plan. For example, "Plenty of parking" or "Free gift on your birthday."'
    )
    .optional(),
  pricing: z
    .intersection(
      z.object({
        price: z
          .object({
            value: z
              .string()
              .describe(
                "Monetary amount. Decimal string with a period as a decimal separator. For example, `'3.99'`. Can't be negative."
              )
              .optional(),
            currency: z
              .string()
              .describe(
                "Currency code. Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `'USD'`."
              )
              .optional(),
          })
          .describe(
            'Amount for a single payment. For subscriptions, this is the amount to pay each payment cycle and it is required. For plans that are not recurring plans, it is the single payment amount for the whole subscription.'
          )
          .optional(),
        freeTrialDays: z
          .number()
          .int()
          .describe(
            'Free trial period for the plan in days. Available only for recurring plans, meaning plans whose pricing model is `subscription`. Set to `0` to remove the free trial.'
          )
          .min(0)
          .max(999)
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          subscription: z.never().optional(),
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
        }),
        z.object({
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
          subscription: z
            .object({
              cycleDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                )
                .optional(),
              cycleCount: z
                .number()
                .int()
                .describe(
                  "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                )
                .min(0)
                .max(2000)
                .optional()
                .nullable(),
            })
            .describe(
              'Pricing model indicating that the plan has recurring payments. This type of subscription is not a "Wix subscription," which encompasses various types of subscriptions, such as Wix Stores subscriptions, Wix invoice subscriptions, and all pricing plan models.'
            ),
        }),
        z.object({
          subscription: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
          singlePaymentForDuration: z
            .object({
              count: z
                .number()
                .int()
                .describe(
                  'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                )
                .min(1)
                .optional()
                .nullable(),
              unit: z
                .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                .describe('Unit of time for the cycle duration.')
                .optional(),
            })
            .describe(
              'Pricing model indicating a single payment per cycle and the length of the cycle. The cycle is the duration of the plan, not a payment cycle.'
            ),
        }),
        z.object({
          subscription: z.never().optional(),
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z
            .boolean()
            .describe(
              'Pricing model indicating the plan is paid in a single payment. The plan is valid until canceled.'
            ),
        }),
      ])
    )
    .describe('Plan price, payment schedule, and expiration.')
    .optional(),
  public: z
    .boolean()
    .describe(
      'Whether the plan is public (visible to site visitors and members).'
    )
    .optional()
    .nullable(),
  archived: z
    .boolean()
    .describe(
      "Whether the plan is archived. Archived plans are not visible and can't be purchased anymore, but existing purchases remain in effect."
    )
    .optional(),
  primary: z
    .boolean()
    .describe('Whether the plan is marked as primary.')
    .optional(),
  hasOrders: z
    .boolean()
    .describe(
      'Whether the plan has any orders (including pending and unpaid orders).'
    )
    .optional(),
  _createdDate: z
    .date()
    .describe('Date plan was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date plan was last updated.')
    .optional()
    .nullable(),
  slug: z
    .string()
    .describe(
      'URL-friendly version of plan name. Unique across all plans in the same site.'
    )
    .min(1)
    .optional()
    .nullable(),
  maxPurchasesPerBuyer: z
    .number()
    .int()
    .describe(
      'Number of times the same buyer can purchase the plan. Currently limited to support:\n- Empty value or a value of `0`, meaning no limitation.\n- Value of `1`, meaning limited to 1 purchase per buyer.'
    )
    .min(0)
    .max(1)
    .optional()
    .nullable(),
  allowFutureStartDate: z
    .boolean()
    .describe(
      'Whether the buyer can start the plan at a later date. Defaults to false.'
    )
    .optional()
    .nullable(),
  buyerCanCancel: z
    .boolean()
    .describe(
      'Whether the buyer is allowed to cancel their plan. Defaults to false.\nIf false, calling Request Cancellation returns an error.'
    )
    .optional()
    .nullable(),
  termsAndConditions: z
    .string()
    .describe(
      'Any terms and conditions that apply to the plan. This information is displayed during checkout.'
    )
    .max(3000)
    .optional()
    .nullable(),
  formId: z
    .string()
    .describe(
      'ID of the form associated with the plan at checkout.\n\nLearn more about [forms](https://support.wix.com/en/article/wix-forms-an-overview).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
});
export const UpdatePlanRequest = z.object({
  _id: z
    .string()
    .describe('ID of the plan to update.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  plan: z
    .object({
      _id: z
        .string()
        .describe('Plan ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      name: z
        .string()
        .describe('Plan name.')
        .min(1)
        .max(50)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe('Plan description.')
        .max(450)
        .optional()
        .nullable(),
      perks: z
        .object({ values: z.array(z.string()).max(500).optional() })
        .describe(
          'List of text strings that promote what is included with this plan. For example, "Plenty of parking" or "Free gift on your birthday."'
        )
        .optional(),
      pricing: z
        .intersection(
          z.object({
            price: z
              .object({
                value: z
                  .string()
                  .describe(
                    "Monetary amount. Decimal string with a period as a decimal separator. For example, `'3.99'`. Can't be negative."
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    "Currency code. Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `'USD'`."
                  )
                  .optional(),
              })
              .describe(
                'Amount for a single payment. For subscriptions, this is the amount to pay each payment cycle and it is required. For plans that are not recurring plans, it is the single payment amount for the whole subscription.'
              )
              .optional(),
            freeTrialDays: z
              .number()
              .int()
              .describe(
                'Free trial period for the plan in days. Available only for recurring plans, meaning plans whose pricing model is `subscription`. Set to `0` to remove the free trial.'
              )
              .min(0)
              .max(999)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
            }),
            z.object({
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              subscription: z
                .object({
                  cycleDuration: z
                    .object({
                      count: z
                        .number()
                        .int()
                        .describe(
                          'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      unit: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Unit of time for the cycle duration.')
                        .optional(),
                    })
                    .describe(
                      'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                    )
                    .optional(),
                  cycleCount: z
                    .number()
                    .int()
                    .describe(
                      "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                    )
                    .min(0)
                    .max(2000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Pricing model indicating that the plan has recurring payments. This type of subscription is not a "Wix subscription," which encompasses various types of subscriptions, such as Wix Stores subscriptions, Wix invoice subscriptions, and all pricing plan models.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              singlePaymentForDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'Pricing model indicating a single payment per cycle and the length of the cycle. The cycle is the duration of the plan, not a payment cycle.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z
                .boolean()
                .describe(
                  'Pricing model indicating the plan is paid in a single payment. The plan is valid until canceled.'
                ),
            }),
          ])
        )
        .describe('Plan price, payment schedule, and expiration.')
        .optional(),
      public: z
        .boolean()
        .describe(
          'Whether the plan is public (visible to site visitors and members).'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the plan is archived. Archived plans are not visible and can't be purchased anymore, but existing purchases remain in effect."
        )
        .optional(),
      primary: z
        .boolean()
        .describe('Whether the plan is marked as primary.')
        .optional(),
      hasOrders: z
        .boolean()
        .describe(
          'Whether the plan has any orders (including pending and unpaid orders).'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date plan was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date plan was last updated.')
        .optional()
        .nullable(),
      slug: z
        .string()
        .describe(
          'URL-friendly version of plan name. Unique across all plans in the same site.'
        )
        .min(1)
        .optional()
        .nullable(),
      maxPurchasesPerBuyer: z
        .number()
        .int()
        .describe(
          'Number of times the same buyer can purchase the plan. Currently limited to support:\n- Empty value or a value of `0`, meaning no limitation.\n- Value of `1`, meaning limited to 1 purchase per buyer.'
        )
        .min(0)
        .max(1)
        .optional()
        .nullable(),
      allowFutureStartDate: z
        .boolean()
        .describe(
          'Whether the buyer can start the plan at a later date. Defaults to false.'
        )
        .optional()
        .nullable(),
      buyerCanCancel: z
        .boolean()
        .describe(
          'Whether the buyer is allowed to cancel their plan. Defaults to false.\nIf false, calling Request Cancellation returns an error.'
        )
        .optional()
        .nullable(),
      termsAndConditions: z
        .string()
        .describe(
          'Any terms and conditions that apply to the plan. This information is displayed during checkout.'
        )
        .max(3000)
        .optional()
        .nullable(),
      formId: z
        .string()
        .describe(
          'ID of the form associated with the plan at checkout.\n\nLearn more about [forms](https://support.wix.com/en/article/wix-forms-an-overview).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe('ID of the plan to update.'),
});
export const UpdatePlanResponse = z.object({
  _id: z
    .string()
    .describe('Plan ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  name: z.string().describe('Plan name.').min(1).max(50).optional().nullable(),
  description: z
    .string()
    .describe('Plan description.')
    .max(450)
    .optional()
    .nullable(),
  perks: z
    .object({ values: z.array(z.string()).max(500).optional() })
    .describe(
      'List of text strings that promote what is included with this plan. For example, "Plenty of parking" or "Free gift on your birthday."'
    )
    .optional(),
  pricing: z
    .intersection(
      z.object({
        price: z
          .object({
            value: z
              .string()
              .describe(
                "Monetary amount. Decimal string with a period as a decimal separator. For example, `'3.99'`. Can't be negative."
              )
              .optional(),
            currency: z
              .string()
              .describe(
                "Currency code. Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `'USD'`."
              )
              .optional(),
          })
          .describe(
            'Amount for a single payment. For subscriptions, this is the amount to pay each payment cycle and it is required. For plans that are not recurring plans, it is the single payment amount for the whole subscription.'
          )
          .optional(),
        freeTrialDays: z
          .number()
          .int()
          .describe(
            'Free trial period for the plan in days. Available only for recurring plans, meaning plans whose pricing model is `subscription`. Set to `0` to remove the free trial.'
          )
          .min(0)
          .max(999)
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({
          subscription: z.never().optional(),
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
        }),
        z.object({
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
          subscription: z
            .object({
              cycleDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                )
                .optional(),
              cycleCount: z
                .number()
                .int()
                .describe(
                  "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                )
                .min(0)
                .max(2000)
                .optional()
                .nullable(),
            })
            .describe(
              'Pricing model indicating that the plan has recurring payments. This type of subscription is not a "Wix subscription," which encompasses various types of subscriptions, such as Wix Stores subscriptions, Wix invoice subscriptions, and all pricing plan models.'
            ),
        }),
        z.object({
          subscription: z.never().optional(),
          singlePaymentUnlimited: z.never().optional(),
          singlePaymentForDuration: z
            .object({
              count: z
                .number()
                .int()
                .describe(
                  'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                )
                .min(1)
                .optional()
                .nullable(),
              unit: z
                .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                .describe('Unit of time for the cycle duration.')
                .optional(),
            })
            .describe(
              'Pricing model indicating a single payment per cycle and the length of the cycle. The cycle is the duration of the plan, not a payment cycle.'
            ),
        }),
        z.object({
          subscription: z.never().optional(),
          singlePaymentForDuration: z.never().optional(),
          singlePaymentUnlimited: z
            .boolean()
            .describe(
              'Pricing model indicating the plan is paid in a single payment. The plan is valid until canceled.'
            ),
        }),
      ])
    )
    .describe('Plan price, payment schedule, and expiration.')
    .optional(),
  public: z
    .boolean()
    .describe(
      'Whether the plan is public (visible to site visitors and members).'
    )
    .optional()
    .nullable(),
  archived: z
    .boolean()
    .describe(
      "Whether the plan is archived. Archived plans are not visible and can't be purchased anymore, but existing purchases remain in effect."
    )
    .optional(),
  primary: z
    .boolean()
    .describe('Whether the plan is marked as primary.')
    .optional(),
  hasOrders: z
    .boolean()
    .describe(
      'Whether the plan has any orders (including pending and unpaid orders).'
    )
    .optional(),
  _createdDate: z
    .date()
    .describe('Date plan was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date plan was last updated.')
    .optional()
    .nullable(),
  slug: z
    .string()
    .describe(
      'URL-friendly version of plan name. Unique across all plans in the same site.'
    )
    .min(1)
    .optional()
    .nullable(),
  maxPurchasesPerBuyer: z
    .number()
    .int()
    .describe(
      'Number of times the same buyer can purchase the plan. Currently limited to support:\n- Empty value or a value of `0`, meaning no limitation.\n- Value of `1`, meaning limited to 1 purchase per buyer.'
    )
    .min(0)
    .max(1)
    .optional()
    .nullable(),
  allowFutureStartDate: z
    .boolean()
    .describe(
      'Whether the buyer can start the plan at a later date. Defaults to false.'
    )
    .optional()
    .nullable(),
  buyerCanCancel: z
    .boolean()
    .describe(
      'Whether the buyer is allowed to cancel their plan. Defaults to false.\nIf false, calling Request Cancellation returns an error.'
    )
    .optional()
    .nullable(),
  termsAndConditions: z
    .string()
    .describe(
      'Any terms and conditions that apply to the plan. This information is displayed during checkout.'
    )
    .max(3000)
    .optional()
    .nullable(),
  formId: z
    .string()
    .describe(
      'ID of the form associated with the plan at checkout.\n\nLearn more about [forms](https://support.wix.com/en/article/wix-forms-an-overview).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
});
export const SetPlanVisibilityRequest = z.object({
  _id: z
    .string()
    .describe('The ID of the plan to either display or hide on the site page. ')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  visible: z
    .boolean()
    .describe(
      'Whether a plan is visible to members and site visitors. Updates the `public` field.'
    ),
});
export const SetPlanVisibilityResponse = z.object({
  plan: z
    .object({
      _id: z
        .string()
        .describe('Plan ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      name: z
        .string()
        .describe('Plan name.')
        .min(1)
        .max(50)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe('Plan description.')
        .max(450)
        .optional()
        .nullable(),
      perks: z
        .object({ values: z.array(z.string()).max(500).optional() })
        .describe(
          'List of text strings that promote what is included with this plan. For example, "Plenty of parking" or "Free gift on your birthday."'
        )
        .optional(),
      pricing: z
        .intersection(
          z.object({
            price: z
              .object({
                value: z
                  .string()
                  .describe(
                    "Monetary amount. Decimal string with a period as a decimal separator. For example, `'3.99'`. Can't be negative."
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    "Currency code. Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `'USD'`."
                  )
                  .optional(),
              })
              .describe(
                'Amount for a single payment. For subscriptions, this is the amount to pay each payment cycle and it is required. For plans that are not recurring plans, it is the single payment amount for the whole subscription.'
              )
              .optional(),
            freeTrialDays: z
              .number()
              .int()
              .describe(
                'Free trial period for the plan in days. Available only for recurring plans, meaning plans whose pricing model is `subscription`. Set to `0` to remove the free trial.'
              )
              .min(0)
              .max(999)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
            }),
            z.object({
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              subscription: z
                .object({
                  cycleDuration: z
                    .object({
                      count: z
                        .number()
                        .int()
                        .describe(
                          'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      unit: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Unit of time for the cycle duration.')
                        .optional(),
                    })
                    .describe(
                      'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                    )
                    .optional(),
                  cycleCount: z
                    .number()
                    .int()
                    .describe(
                      "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                    )
                    .min(0)
                    .max(2000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Pricing model indicating that the plan has recurring payments. This type of subscription is not a "Wix subscription," which encompasses various types of subscriptions, such as Wix Stores subscriptions, Wix invoice subscriptions, and all pricing plan models.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              singlePaymentForDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'Pricing model indicating a single payment per cycle and the length of the cycle. The cycle is the duration of the plan, not a payment cycle.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z
                .boolean()
                .describe(
                  'Pricing model indicating the plan is paid in a single payment. The plan is valid until canceled.'
                ),
            }),
          ])
        )
        .describe('Plan price, payment schedule, and expiration.')
        .optional(),
      public: z
        .boolean()
        .describe(
          'Whether the plan is public (visible to site visitors and members).'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the plan is archived. Archived plans are not visible and can't be purchased anymore, but existing purchases remain in effect."
        )
        .optional(),
      primary: z
        .boolean()
        .describe('Whether the plan is marked as primary.')
        .optional(),
      hasOrders: z
        .boolean()
        .describe(
          'Whether the plan has any orders (including pending and unpaid orders).'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date plan was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date plan was last updated.')
        .optional()
        .nullable(),
      slug: z
        .string()
        .describe(
          'URL-friendly version of plan name. Unique across all plans in the same site.'
        )
        .min(1)
        .optional()
        .nullable(),
      maxPurchasesPerBuyer: z
        .number()
        .int()
        .describe(
          'Number of times the same buyer can purchase the plan. Currently limited to support:\n- Empty value or a value of `0`, meaning no limitation.\n- Value of `1`, meaning limited to 1 purchase per buyer.'
        )
        .min(0)
        .max(1)
        .optional()
        .nullable(),
      allowFutureStartDate: z
        .boolean()
        .describe(
          'Whether the buyer can start the plan at a later date. Defaults to false.'
        )
        .optional()
        .nullable(),
      buyerCanCancel: z
        .boolean()
        .describe(
          'Whether the buyer is allowed to cancel their plan. Defaults to false.\nIf false, calling Request Cancellation returns an error.'
        )
        .optional()
        .nullable(),
      termsAndConditions: z
        .string()
        .describe(
          'Any terms and conditions that apply to the plan. This information is displayed during checkout.'
        )
        .max(3000)
        .optional()
        .nullable(),
      formId: z
        .string()
        .describe(
          'ID of the form associated with the plan at checkout.\n\nLearn more about [forms](https://support.wix.com/en/article/wix-forms-an-overview).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe('Pricing plan.')
    .optional(),
});
export const MakePlanPrimaryRequest = z.object({
  _id: z
    .string()
    .describe('ID of the pricing plan to set as the primary plan.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const MakePlanPrimaryResponse = z.object({
  plan: z
    .object({
      _id: z
        .string()
        .describe('Plan ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      name: z
        .string()
        .describe('Plan name.')
        .min(1)
        .max(50)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe('Plan description.')
        .max(450)
        .optional()
        .nullable(),
      perks: z
        .object({ values: z.array(z.string()).max(500).optional() })
        .describe(
          'List of text strings that promote what is included with this plan. For example, "Plenty of parking" or "Free gift on your birthday."'
        )
        .optional(),
      pricing: z
        .intersection(
          z.object({
            price: z
              .object({
                value: z
                  .string()
                  .describe(
                    "Monetary amount. Decimal string with a period as a decimal separator. For example, `'3.99'`. Can't be negative."
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    "Currency code. Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `'USD'`."
                  )
                  .optional(),
              })
              .describe(
                'Amount for a single payment. For subscriptions, this is the amount to pay each payment cycle and it is required. For plans that are not recurring plans, it is the single payment amount for the whole subscription.'
              )
              .optional(),
            freeTrialDays: z
              .number()
              .int()
              .describe(
                'Free trial period for the plan in days. Available only for recurring plans, meaning plans whose pricing model is `subscription`. Set to `0` to remove the free trial.'
              )
              .min(0)
              .max(999)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
            }),
            z.object({
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              subscription: z
                .object({
                  cycleDuration: z
                    .object({
                      count: z
                        .number()
                        .int()
                        .describe(
                          'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      unit: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Unit of time for the cycle duration.')
                        .optional(),
                    })
                    .describe(
                      'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                    )
                    .optional(),
                  cycleCount: z
                    .number()
                    .int()
                    .describe(
                      "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                    )
                    .min(0)
                    .max(2000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Pricing model indicating that the plan has recurring payments. This type of subscription is not a "Wix subscription," which encompasses various types of subscriptions, such as Wix Stores subscriptions, Wix invoice subscriptions, and all pricing plan models.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              singlePaymentForDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'Pricing model indicating a single payment per cycle and the length of the cycle. The cycle is the duration of the plan, not a payment cycle.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z
                .boolean()
                .describe(
                  'Pricing model indicating the plan is paid in a single payment. The plan is valid until canceled.'
                ),
            }),
          ])
        )
        .describe('Plan price, payment schedule, and expiration.')
        .optional(),
      public: z
        .boolean()
        .describe(
          'Whether the plan is public (visible to site visitors and members).'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the plan is archived. Archived plans are not visible and can't be purchased anymore, but existing purchases remain in effect."
        )
        .optional(),
      primary: z
        .boolean()
        .describe('Whether the plan is marked as primary.')
        .optional(),
      hasOrders: z
        .boolean()
        .describe(
          'Whether the plan has any orders (including pending and unpaid orders).'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date plan was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date plan was last updated.')
        .optional()
        .nullable(),
      slug: z
        .string()
        .describe(
          'URL-friendly version of plan name. Unique across all plans in the same site.'
        )
        .min(1)
        .optional()
        .nullable(),
      maxPurchasesPerBuyer: z
        .number()
        .int()
        .describe(
          'Number of times the same buyer can purchase the plan. Currently limited to support:\n- Empty value or a value of `0`, meaning no limitation.\n- Value of `1`, meaning limited to 1 purchase per buyer.'
        )
        .min(0)
        .max(1)
        .optional()
        .nullable(),
      allowFutureStartDate: z
        .boolean()
        .describe(
          'Whether the buyer can start the plan at a later date. Defaults to false.'
        )
        .optional()
        .nullable(),
      buyerCanCancel: z
        .boolean()
        .describe(
          'Whether the buyer is allowed to cancel their plan. Defaults to false.\nIf false, calling Request Cancellation returns an error.'
        )
        .optional()
        .nullable(),
      termsAndConditions: z
        .string()
        .describe(
          'Any terms and conditions that apply to the plan. This information is displayed during checkout.'
        )
        .max(3000)
        .optional()
        .nullable(),
      formId: z
        .string()
        .describe(
          'ID of the form associated with the plan at checkout.\n\nLearn more about [forms](https://support.wix.com/en/article/wix-forms-an-overview).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe('Pricing plan.')
    .optional(),
});
export const ClearPrimaryRequest = z.object({});
export const ClearPrimaryResponse = z.object({});
export const ArchivePlanRequest = z.object({
  _id: z
    .string()
    .describe('ID of the active plan to archive. ')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const ArchivePlanResponse = z.object({
  plan: z
    .object({
      _id: z
        .string()
        .describe('Plan ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      name: z
        .string()
        .describe('Plan name.')
        .min(1)
        .max(50)
        .optional()
        .nullable(),
      description: z
        .string()
        .describe('Plan description.')
        .max(450)
        .optional()
        .nullable(),
      perks: z
        .object({ values: z.array(z.string()).max(500).optional() })
        .describe(
          'List of text strings that promote what is included with this plan. For example, "Plenty of parking" or "Free gift on your birthday."'
        )
        .optional(),
      pricing: z
        .intersection(
          z.object({
            price: z
              .object({
                value: z
                  .string()
                  .describe(
                    "Monetary amount. Decimal string with a period as a decimal separator. For example, `'3.99'`. Can't be negative."
                  )
                  .optional(),
                currency: z
                  .string()
                  .describe(
                    "Currency code. Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `'USD'`."
                  )
                  .optional(),
              })
              .describe(
                'Amount for a single payment. For subscriptions, this is the amount to pay each payment cycle and it is required. For plans that are not recurring plans, it is the single payment amount for the whole subscription.'
              )
              .optional(),
            freeTrialDays: z
              .number()
              .int()
              .describe(
                'Free trial period for the plan in days. Available only for recurring plans, meaning plans whose pricing model is `subscription`. Set to `0` to remove the free trial.'
              )
              .min(0)
              .max(999)
              .optional()
              .nullable(),
          }),
          z.xor([
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
            }),
            z.object({
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              subscription: z
                .object({
                  cycleDuration: z
                    .object({
                      count: z
                        .number()
                        .int()
                        .describe(
                          'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                        )
                        .min(1)
                        .optional()
                        .nullable(),
                      unit: z
                        .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                        .describe('Unit of time for the cycle duration.')
                        .optional(),
                    })
                    .describe(
                      'Number of payment cycles the subscription is valid for.\n`0` for unlimited plans or for plans that are valid until canceled.'
                    )
                    .optional(),
                  cycleCount: z
                    .number()
                    .int()
                    .describe(
                      "Length of a payment cycle. For example, 1 month to have monthly payments.\nMultiply `cycleDuration`'s `count` by `cycleCount` to get the subscription duration.\nCurrently, only a value of `1` is supported."
                    )
                    .min(0)
                    .max(2000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Pricing model indicating that the plan has recurring payments. This type of subscription is not a "Wix subscription," which encompasses various types of subscriptions, such as Wix Stores subscriptions, Wix invoice subscriptions, and all pricing plan models.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentUnlimited: z.never().optional(),
              singlePaymentForDuration: z
                .object({
                  count: z
                    .number()
                    .int()
                    .describe(
                      'Number of days, months, weeks, or years in a single payment cycle. Currently limited to support only `1`.'
                    )
                    .min(1)
                    .optional()
                    .nullable(),
                  unit: z
                    .enum(['UNDEFINED', 'DAY', 'WEEK', 'MONTH', 'YEAR'])
                    .describe('Unit of time for the cycle duration.')
                    .optional(),
                })
                .describe(
                  'Pricing model indicating a single payment per cycle and the length of the cycle. The cycle is the duration of the plan, not a payment cycle.'
                ),
            }),
            z.object({
              subscription: z.never().optional(),
              singlePaymentForDuration: z.never().optional(),
              singlePaymentUnlimited: z
                .boolean()
                .describe(
                  'Pricing model indicating the plan is paid in a single payment. The plan is valid until canceled.'
                ),
            }),
          ])
        )
        .describe('Plan price, payment schedule, and expiration.')
        .optional(),
      public: z
        .boolean()
        .describe(
          'Whether the plan is public (visible to site visitors and members).'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe(
          "Whether the plan is archived. Archived plans are not visible and can't be purchased anymore, but existing purchases remain in effect."
        )
        .optional(),
      primary: z
        .boolean()
        .describe('Whether the plan is marked as primary.')
        .optional(),
      hasOrders: z
        .boolean()
        .describe(
          'Whether the plan has any orders (including pending and unpaid orders).'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date plan was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date plan was last updated.')
        .optional()
        .nullable(),
      slug: z
        .string()
        .describe(
          'URL-friendly version of plan name. Unique across all plans in the same site.'
        )
        .min(1)
        .optional()
        .nullable(),
      maxPurchasesPerBuyer: z
        .number()
        .int()
        .describe(
          'Number of times the same buyer can purchase the plan. Currently limited to support:\n- Empty value or a value of `0`, meaning no limitation.\n- Value of `1`, meaning limited to 1 purchase per buyer.'
        )
        .min(0)
        .max(1)
        .optional()
        .nullable(),
      allowFutureStartDate: z
        .boolean()
        .describe(
          'Whether the buyer can start the plan at a later date. Defaults to false.'
        )
        .optional()
        .nullable(),
      buyerCanCancel: z
        .boolean()
        .describe(
          'Whether the buyer is allowed to cancel their plan. Defaults to false.\nIf false, calling Request Cancellation returns an error.'
        )
        .optional()
        .nullable(),
      termsAndConditions: z
        .string()
        .describe(
          'Any terms and conditions that apply to the plan. This information is displayed during checkout.'
        )
        .max(3000)
        .optional()
        .nullable(),
      formId: z
        .string()
        .describe(
          'ID of the form associated with the plan at checkout.\n\nLearn more about [forms](https://support.wix.com/en/article/wix-forms-an-overview).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe('Archived plan.')
    .optional(),
});
